import { createElement as _createElement } from "react";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { flexRender, } from '@tanstack/react-table';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { classNames } from '../_common/defaultImports';
import { IconButton } from '../button';
import { Menu } from '../context-menu';
import { Dropdown } from '../dropdown';
import { Bars3CenterLeftIconOutline, Bars3CenterLeftIconSolid, BarsArrowDownIconOutline, BarsArrowUpIconOutline, ChevronDownIconSolid, ChevronLeftIconOutline, ChevronRightIconOutline, EllipsisHorizontalIconOutline, } from '../icons';
import { LoadingSpinner } from '../loading-spinner';
import { useDataGridContext } from './DataGridContext';
import { isSizeConstrained, userFriendlyPagination } from './helpers';
/** Table results per page, with the first option being the default */
const TABLE_RESULTS = {
    DEFAULT: 10,
    values: [10, 25, 50, 100],
};
export const DEFAULT_SORT_ACTIONS = (cell) => ({
    asc: {
        title: 'Sort ascending',
        icon: _jsx(BarsArrowUpIconOutline, {}),
        onClick: () => cell.column.toggleSorting(false),
    },
    desc: {
        title: 'Sort descending',
        icon: _jsx(BarsArrowDownIconOutline, {}),
        onClick: () => cell.column.toggleSorting(true),
    },
    default: {
        title: 'Default sort',
        icon: _jsx(Bars3CenterLeftIconOutline, {}),
        onClick: () => cell.column.clearSorting(),
    },
});
const ResizingBar = ({ header, children, innerProps, }) => {
    const { className = undefined } = innerProps || {};
    const isLastColumnId = useCallback((headers, columnId) => {
        const columnIndex = headers.findIndex((header) => header.id === columnId);
        return columnIndex === headers.length - 1;
    }, []);
    let ResizeDirection;
    (function (ResizeDirection) {
        ResizeDirection["LEFT"] = "left";
        ResizeDirection["RIGHT"] = "right";
    })(ResizeDirection || (ResizeDirection = {}));
    const resizeColumn = useCallback((direction) => {
        var _a, _b, _c;
        const RESIZE_AMOUNT = 5; // Pixels to shift
        const currentColumnId = header.column.id;
        const table = header.getContext().table;
        const maxColumnWidth = (_a = header.column.columnDef.maxSize) !== null && _a !== void 0 ? _a : window.innerWidth;
        const minColumnWidth = (_b = header.column.columnDef.minSize) !== null && _b !== void 0 ? _b : 0;
        const maxTableRowWidth = ((_c = document.querySelector('.ndl-data-grid-thead')) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect().width) || 0;
        const adjustColumnWidth = (currentWidth, adjustment) => {
            return Math.min(Math.max(currentWidth + adjustment, minColumnWidth), maxColumnWidth);
        };
        if (direction === ResizeDirection.LEFT) {
            table.setColumnSizing((currentSizes) => {
                const newWidth = adjustColumnWidth(currentSizes[currentColumnId], -RESIZE_AMOUNT);
                return Object.assign(Object.assign({}, currentSizes), { [currentColumnId]: newWidth });
            });
        }
        else if (direction === ResizeDirection.RIGHT) {
            table.setColumnSizing((currentSizes) => {
                if (isLastColumnId(header.headerGroup.headers, currentColumnId)) {
                    // Calculate the total width of the current columns combined
                    const totalCurrentWidth = Object.values(currentSizes).reduce((total, size) => total + (isNaN(size) ? 0 : size), 0);
                    if (totalCurrentWidth + RESIZE_AMOUNT >= maxTableRowWidth) {
                        return currentSizes;
                    }
                }
                const newWidth = adjustColumnWidth(currentSizes[currentColumnId], RESIZE_AMOUNT);
                return Object.assign(Object.assign({}, currentSizes), { [currentColumnId]: newWidth });
            });
        }
    }, [header, isLastColumnId, ResizeDirection]);
    const handleKeyDown = useCallback((event) => {
        if (event.key === 'ArrowLeft') {
            event.stopPropagation();
            event.preventDefault();
            resizeColumn(ResizeDirection.LEFT);
        }
        else if (event.key === 'ArrowRight') {
            event.stopPropagation();
            event.preventDefault();
            resizeColumn(ResizeDirection.RIGHT);
        }
    }, [resizeColumn, ResizeDirection]);
    return (_jsx(_Fragment, { children: children || (_jsx("button", Object.assign({}, innerProps, { type: "button", className: "ndl-data-grid-resizer-wrapper", onMouseDown: header.getResizeHandler(), onTouchStart: header.getResizeHandler(), onKeyDown: handleKeyDown, "aria-label": "resizing bar", children: _jsx("div", { className: classNames('ndl-data-grid-resizer', className, {
                    'ndl-data-grid-is-resizing': header.column.getIsResizing(),
                }) }) }))) }));
};
const Scrollable = ({ children, innerProps }) => {
    const { className } = innerProps || {};
    return (_jsx("div", Object.assign({}, innerProps, { className: classNames('ndl-data-grid-scrollable', className), children: children })));
};
const Header = ({ children, innerProps }) => {
    const { tableProps, components } = useDataGridContext();
    const { getHeaderGroups, getState } = tableProps;
    const { className = undefined } = innerProps || {};
    return (_jsx("div", Object.assign({}, innerProps, { role: "rowgroup", className: classNames('ndl-data-grid-thead', className, {
            'ndl-data-grid-is-resizing': getState().columnSizingInfo.isResizingColumn,
        }), children: children || (_jsx(_Fragment, { children: getHeaderGroups().map((headerGroup) => (_jsx("div", { className: "ndl-data-grid-tr", role: "row", children: headerGroup.headers.map((header) => components.HeaderCell && (_jsx(components.HeaderCell, { cell: header }, header.id))) }, headerGroup.id))) })) })));
};
export const ActionButton = ({ action, children, onClose, innerProps, }) => {
    const { className } = innerProps || {};
    const { onClick } = action;
    const interceptedOnClick = useCallback((e) => {
        const providedOnClick = onClick || (innerProps === null || innerProps === void 0 ? void 0 : innerProps.onClick);
        if (typeof providedOnClick === 'function') {
            providedOnClick(e);
        }
        onClose();
    }, [innerProps === null || innerProps === void 0 ? void 0 : innerProps.onClick, onClick, onClose]);
    // const commonProps: MenuItemProps = {
    const commonProps = Object.assign(Object.assign(Object.assign({}, innerProps), action), { className: classNames('ndl-data-grid-column-action', className), onClick: interceptedOnClick });
    return _jsx(Fragment, { children: children || _jsx(Menu.Item, Object.assign({}, commonProps)) });
};
const RowActionCell = ({ cell, innerIconButtonProps, innerMenuProps, }) => {
    var _a, _b;
    const { components } = useDataGridContext();
    const actionsButtonRef = useRef(null);
    const [actionsOpen, setActionsOpen] = useState(false);
    if (!cell ||
        !cell.column.columnDef.meta ||
        typeof ((_b = (_a = cell.column.columnDef) === null || _a === void 0 ? void 0 : _a.meta) === null || _b === void 0 ? void 0 : _b.isActionCell) !== 'object')
        return null;
    const { actions, onOpenChange } = cell.column.columnDef.meta.isActionCell;
    return (_jsxs(Fragment, { children: [_jsx(IconButton, Object.assign({ clean: true, size: "medium", "aria-label": `Row actions for row id: ${cell.row.id}`, ref: actionsButtonRef, active: actionsOpen, onClick: () => {
                    onOpenChange && onOpenChange(!actionsOpen);
                    setActionsOpen(!actionsOpen);
                } }, innerIconButtonProps, { children: (innerIconButtonProps === null || innerIconButtonProps === void 0 ? void 0 : innerIconButtonProps.children) || _jsx(EllipsisHorizontalIconOutline, {}) })), _jsx(Menu, Object.assign({ open: actionsOpen, anchorEl: actionsButtonRef.current, onClose: () => {
                    onOpenChange && onOpenChange(false);
                    setActionsOpen(false);
                }, anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                }, transformOrigin: {
                    horizontal: 'right',
                    vertical: 'top',
                } }, innerMenuProps, { children: (innerMenuProps === null || innerMenuProps === void 0 ? void 0 : innerMenuProps.children) ||
                    actions.map((action, i) => components.ActionButton && (_jsx(components.ActionButton, { action: Object.assign(Object.assign({}, action), { onClick: (e) => {
                                action.onClick && action.onClick(e, cell);
                            } }), onClose: () => setActionsOpen(false) }, `${cell.column.id}-action-${i}`))) }))] }));
};
export const ColumnControls = ({ cell, children, innerProps, }) => {
    var _a;
    const { className } = innerProps || {};
    const { components } = useDataGridContext();
    const sort = cell.column.getIsSorted();
    const canSort = cell.column.getCanSort();
    const { defaultSortingActions = true, actions } = ((_a = cell.column.columnDef.meta) === null || _a === void 0 ? void 0 : _a.columnActions) || {};
    const sortingActions = useMemo(() => {
        if (canSort) {
            if (sort !== 'asc' && sort !== 'desc') {
                return [
                    DEFAULT_SORT_ACTIONS(cell).asc,
                    DEFAULT_SORT_ACTIONS(cell).desc,
                ];
            }
            if (sort === 'asc') {
                return [
                    DEFAULT_SORT_ACTIONS(cell).default,
                    DEFAULT_SORT_ACTIONS(cell).desc,
                ];
            }
            if (sort === 'desc') {
                return [
                    DEFAULT_SORT_ACTIONS(cell).default,
                    DEFAULT_SORT_ACTIONS(cell).asc,
                ];
            }
        }
        // Default case
        return [];
    }, [sort, cell, canSort]);
    const newActions = [
        ...(defaultSortingActions ? sortingActions : []),
        ...(actions !== null && actions !== void 0 ? actions : []),
    ];
    const actionsButtonRef = useRef(null);
    const [actionsOpen, setActionsOpen] = useState(false);
    if (!actions || actions.length === 0)
        return null;
    return newActions.length > 0 ? (_jsx("div", Object.assign({}, innerProps, { className: classNames('ndl-header-action-group', className), style: {
            zIndex: 100000,
            position: 'relative',
            right: '12px',
        }, children: children || (_jsxs(Fragment, { children: [_jsx(IconButton, { clean: true, size: "medium", "arial-label": `Column actions for ${cell.column.id} column`, ref: actionsButtonRef, active: actionsOpen, onClick: () => {
                        setActionsOpen(!actionsOpen);
                    }, children: _jsx(ChevronDownIconSolid, {}) }), _jsx(Menu, { open: actionsOpen, anchorEl: actionsButtonRef.current, onClose: () => setActionsOpen(false), anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'center',
                    }, transformOrigin: {
                        horizontal: 'center',
                        vertical: 'top',
                    }, children: newActions.map((action, i) => components.ActionButton && (_jsx(components.ActionButton, { action: action, onClose: () => setActionsOpen(false) }, `${cell.column.id}-action-${i}`))) })] })) }))) : null;
};
const HeaderCell = ({ cell, children, innerProps, }) => {
    const { components, resizable, tableProps, resizeColumns, isKeyboardNavigationEnabled, } = useDataGridContext();
    const { className } = innerProps || {};
    const sort = cell.column.getIsSorted();
    const canSort = cell.column.getCanSort();
    const ariaSortRole = useMemo(() => {
        switch (sort) {
            case 'asc':
                return 'ascending';
            case 'desc':
                return 'descending';
            default:
                return 'none';
        }
    }, [sort]);
    const Icon = useMemo(() => {
        switch (sort) {
            case 'asc':
                return _jsx(BarsArrowUpIconOutline, { className: "ndl-header-icon" });
            case 'desc':
                return _jsx(BarsArrowDownIconOutline, { className: "ndl-header-icon" });
            default:
                return (_jsx(Bars3CenterLeftIconSolid, { className: "ndl-hoverable-indicator ndl-header-icon" }));
        }
    }, [sort]);
    const ariaDescription = useMemo(() => {
        return canSort
            ? `Press ENTER or SPACE to sort column ${cell.column.id}`
            : `Column id: ${cell.column.id}`;
    }, [canSort, cell.column.id]);
    const constrained = useMemo(() => isSizeConstrained(cell.column.columnDef), [cell.column.columnDef]);
    const ref = useRef(null);
    const [grow, setGrow] = useState(resizeColumns);
    /**
     * This part enables the auto sizing of the columns.
     *
     * On initial render we need to wait for the width to be
     * calculated by the browser with `flex-grow: 1`, if the size is not
     * constrained by the user.
     * Then we update the table state with the new width to aligned
     * with the one calculated by browser, for optimal user experience.
     */
    const updateColumnSize = useCallback(() => {
        if (resizeColumns && ref.current) {
            const { width } = ref.current.getBoundingClientRect();
            if (width) {
                // Only update state if width has changed
                if (tableProps.getState().columnSizing[cell.column.id] !== width) {
                    tableProps.setState((prev) => (Object.assign(Object.assign({}, prev), { columnSizing: Object.assign(Object.assign({}, prev.columnSizing), { [cell.column.id]: width }) })));
                    // Disable flex grow, to avoid weird resizing behaviour
                    setGrow(false);
                }
            }
        }
    }, [resizeColumns, tableProps, cell.column.id]);
    useEffect(() => {
        updateColumnSize();
    }, [updateColumnSize]);
    const isActionColumn = useMemo(() => { var _a; return typeof ((_a = cell.column.columnDef.meta) === null || _a === void 0 ? void 0 : _a.isActionCell) === 'object'; }, [cell.column.columnDef.meta]);
    const Tag = canSort ? 'button' : 'div';
    const InnerHeaderCell = () => (_jsxs("div", { className: "ndl-header-cell", children: [cell.isPlaceholder
                ? null
                : flexRender(cell.column.columnDef.header, cell.getContext()), canSort && Icon] }));
    return (_jsx("div", Object.assign({ tabIndex: isKeyboardNavigationEnabled ? 0 : -1, role: "columnheader" }, innerProps, { className: classNames('ndl-data-grid-th ndl-focusable-cell', className, {
            'ndl-data-grid-row-action': isActionColumn,
        }), "aria-sort": ariaSortRole, style: {
            width: isActionColumn ? 40 : cell.getSize(),
            maxWidth: cell.column.columnDef.maxSize
                ? `${cell.column.columnDef.maxSize}px`
                : 'none',
            flexGrow: !isActionColumn && !constrained && grow ? 1 : 0,
        }, ref: ref, children: children || (_jsxs(_Fragment, { children: [_jsx(Tag, { className: "ndl-header-group", style: {
                        cursor: canSort ? 'pointer' : 'default',
                    }, tabIndex: canSort ? -1 : undefined, "aria-describedby": ariaDescription, onClick: () => canSort && cell.column.toggleSorting(), children: components.HeaderTitle ? (_jsx(components.HeaderTitle, { cell: cell, children: _jsx(InnerHeaderCell, {}) })) : (_jsx(InnerHeaderCell, {})) }), components.ColumnControls && (_jsx(components.ColumnControls, { cell: cell })), resizable &&
                    cell.column.getCanResize() &&
                    components.ResizingBar && _jsx(components.ResizingBar, { header: cell })] })) }), cell.id));
};
const HeaderTitle = ({ cell, children, innerProps, }) => {
    const { column, id } = cell;
    const title = typeof column.columnDef.header === 'string' ? column.columnDef.header : id;
    return (_jsx("div", Object.assign({}, innerProps, { tabIndex: -1, title: title, children: children })));
};
const Body = ({ children, innerProps }) => {
    const { tableProps, components, loading } = useDataGridContext();
    const { getRowModel } = tableProps;
    const { className } = innerProps || {};
    return (_jsx("div", Object.assign({}, innerProps, { className: classNames('ndl-data-grid-tbody', className), role: "rowgroup", "aria-busy": loading ? 'true' : 'false', children: children || (_jsxs(_Fragment, { children: [loading && components.LoadingPlaceholder && (_jsx(components.LoadingPlaceholder, {})), !loading &&
                    getRowModel().rows.length === 0 &&
                    components.NoDataPlaceholder && _jsx(components.NoDataPlaceholder, {}), !loading &&
                    getRowModel().rows.map((row, idx) => components.BodyRow && (_jsx(components.BodyRow, { row: row }, `table-row-${idx}`)))] })) })));
};
const BodyCell = ({ cell, children, innerProps, }) => {
    var _a, _b, _c;
    const isResizing = cell.column.getIsResizing();
    const { className } = innerProps || {};
    const isActionColumn = Boolean((_a = cell.column.columnDef.meta) === null || _a === void 0 ? void 0 : _a.isActionCell);
    const isInlineEditCell = Boolean((_b = cell.column.columnDef.meta) === null || _b === void 0 ? void 0 : _b.isInlineEditCell);
    const isDropDownCell = Boolean((_c = cell.column.columnDef.meta) === null || _c === void 0 ? void 0 : _c.isDropDownCell);
    const isCustomCell = isActionColumn || isDropDownCell;
    const { isKeyboardNavigationEnabled } = useDataGridContext();
    return (_jsx("div", Object.assign({}, innerProps, { className: classNames('ndl-data-grid-td ndl-focusable-cell', className, {
            'ndl-data-grid-is-resizing': isResizing,
            'ndl-data-grid-custom-cell': isCustomCell,
            'ndl-data-grid-row-action': isActionColumn,
            'ndl-data-grid-inline-edit': isInlineEditCell,
            'ndl-data-grid-dropdown-cell': isDropDownCell,
        }), role: "cell", tabIndex: isKeyboardNavigationEnabled ? 0 : -1, style: {
            width: isActionColumn ? 40 : cell.column.getSize(),
        }, children: children || flexRender(cell.column.columnDef.cell, cell.getContext()) }), cell.id));
};
const BodyRow = ({ row, children, innerProps, }) => {
    const { components } = useDataGridContext();
    const { className, style = {} } = innerProps || {};
    return (_createElement("div", Object.assign({}, innerProps, { key: row.id, className: classNames('ndl-data-grid-tr', className), role: "row", style: Object.assign({ display: 'flex', flexDirection: 'row' }, style) }), children || (_jsx(_Fragment, { children: row
            .getVisibleCells()
            .map((cell) => components.BodyCell && (_jsx(components.BodyCell, { cell: cell }, cell.id))) }))));
};
const TableResults = ({ children, innerProps, manualPagination, }) => {
    const { tableProps } = useDataGridContext();
    const { getState, getRowModel, getCoreRowModel } = tableProps;
    const { pagination: { pageSize, pageIndex }, } = getState();
    const { rows } = getRowModel();
    const { from, to, totalRows } = useMemo(() => manualPagination || {
        from: 1 + pageIndex * pageSize,
        to: rows.length + pageIndex * pageSize,
        totalRows: getCoreRowModel().rows.length,
    }, [pageIndex, pageSize, manualPagination, rows, getCoreRowModel]);
    const { className } = innerProps || {};
    return (_jsx("span", Object.assign({}, innerProps, { className: classNames('ndl-data-grid-results', className), children: children || (_jsxs("span", { children: ["Showing", ' ', _jsx("b", { children: totalRows ? `${from}${to !== from ? `-${to}` : ''}` : 0 }), " of", ' ', _jsx("b", { children: totalRows }), " results"] })) })));
};
const RowsPerPage = ({ children, innerProps, }) => {
    const { tableProps } = useDataGridContext();
    const { setPageSize, getState } = tableProps;
    const { className } = innerProps || {};
    const { pagination: { pageSize }, } = getState();
    return (_jsx("div", Object.assign({}, innerProps, { className: classNames('ndl-data-grid-rows-per-page', className), children: children || (_jsxs(_Fragment, { children: ["Show", _jsx("select", { onChange: (e) => setPageSize(parseInt(e.target.value, 10)), className: "ndl-data-grid-select", value: pageSize, "aria-label": "Select page size", children: [...new Set([...TABLE_RESULTS.values, pageSize])]
                        .sort((a, b) => a - b)
                        .map((option) => (_jsx("option", { value: `${option}`, children: option }, `${option}`))) })] })) })));
};
const PaginationArrowButton = ({ action, children, innerProps, }) => {
    const { className } = innerProps || {};
    return (_jsx("button", Object.assign({}, innerProps, { type: "button", className: classNames('ndl-data-grid-pagination-icon-button', className), children: children || (_jsxs(_Fragment, { children: [action === 'previous' && (_jsxs(_Fragment, { children: [_jsx("span", { className: "n-sr-only", children: "Previous table page" }), _jsx(ChevronLeftIconOutline, { className: "ndl-data-grid-pagination-icon" })] })), action === 'next' && (_jsxs(_Fragment, { children: [_jsx("span", { className: "n-sr-only", children: "Next table page" }), _jsx(ChevronRightIconOutline, { className: "ndl-data-grid-pagination-icon" })] }))] })) })));
};
const PaginationNumericButton = ({ currentIndex, isSelected, children, innerProps, }) => {
    const { className, onClick } = innerProps || {};
    return (_jsx("button", Object.assign({}, innerProps, { type: "button", "data-testid": `ndl-table-${currentIndex}`, className: classNames('ndl-data-grid-pagination-numeric-button', className, {
            'ndl-not-selected-numeric': typeof currentIndex === 'number' && !isSelected,
            'ndl-is-selected': isSelected,
            'ndl-not-selected': !isSelected,
        }), onClick: (e) => typeof currentIndex === 'number' && onClick && onClick(e), tabIndex: typeof currentIndex === 'number' ? 0 : -1, children: children || currentIndex })));
};
const PaginationNumericButtons = () => {
    const { tableProps, components } = useDataGridContext();
    const { getState, setPageIndex, getPageCount } = tableProps;
    const { pagination: { pageIndex }, } = getState();
    return (_jsx(_Fragment, { children: userFriendlyPagination(pageIndex + 1, getPageCount()).map((option, idx) => components.PaginationNumericButton && (_jsx(components.PaginationNumericButton
        /** Use index to avoid situations where two "..." values will appear, causing a key conflict */
        , { innerProps: {
                onClick: () => typeof option === 'number' && setPageIndex(option - 1),
            }, currentIndex: option, isSelected: option === pageIndex + 1 }, `${option}-${idx}`))) }));
};
const Pagination = ({ children, innerProps, }) => {
    const { tableProps, components } = useDataGridContext();
    const { getCanPreviousPage, getCanNextPage, previousPage, nextPage } = tableProps;
    const { className } = innerProps || {};
    return (_jsx("nav", Object.assign({}, innerProps, { className: classNames('ndl-data-grid-nav', className), "aria-label": "Pagination", children: children || (_jsxs(_Fragment, { children: [getCanPreviousPage()
                    ? components.PaginationArrowButton && (_jsx(components.PaginationArrowButton, { action: "previous", innerProps: {
                            onClick: previousPage,
                        } }))
                    : null, components.PaginationNumericButtons && (_jsx(components.PaginationNumericButtons, {})), getCanNextPage()
                    ? components.PaginationArrowButton && (_jsx(components.PaginationArrowButton, { action: "next", innerProps: {
                            onClick: nextPage,
                        } }))
                    : null] })) })));
};
const Navigation = ({ children, innerProps, }) => {
    const { tableProps, components } = useDataGridContext();
    const { getPageCount } = tableProps;
    const { className } = innerProps || {};
    return (_jsx("div", Object.assign({}, innerProps, { className: classNames('ndl-data-grid-navigation', className), children: _jsx(_Fragment, { children: children || (_jsxs(_Fragment, { children: [components.TableResults && _jsx(components.TableResults, {}), _jsxs("div", { className: "ndl-data-grid-navigation-right-items", children: [getPageCount() > 1 && components.Pagination && (_jsx(components.Pagination, {})), components.RowsPerPage && _jsx(components.RowsPerPage, {})] })] })) }) })));
};
const LoadingPlaceholder = ({ children, innerProps, }) => {
    const { className } = innerProps || {};
    return (_jsx("div", Object.assign({}, innerProps, { className: classNames('nld-table-placeholder-wrapper', className), role: "row", children: children || (_jsx("div", { role: "cell", className: "ndl-data-grid-placeholder", children: _jsxs("div", { className: "ndl-data-grid-loading-placeholder", children: [_jsx(LoadingSpinner, {}), _jsx("h6", { children: "Loading data" })] }) })) })));
};
const NoDataPlaceholder = ({ children, innerProps, }) => {
    const { className } = innerProps || {};
    return (_jsx("div", Object.assign({}, innerProps, { className: classNames('nld-table-placeholder-wrapper', className), role: "row", children: children || (_jsx("div", { role: "cell", className: "ndl-data-grid-placeholder", children: _jsx("h6", { children: "No data present" }) })) })));
};
const InlineEditCell = ({ cell, value, }) => {
    var _a;
    const [inputValue, setInputValue] = useState(value);
    useEffect(() => {
        setInputValue(value);
    }, [value]);
    if (!((_a = cell.column.columnDef.meta) === null || _a === void 0 ? void 0 : _a.isInlineEditCell)) {
        return null;
    }
    return (_jsx("input", { onChange: (e) => setInputValue(e.target.value), value: inputValue, onKeyDown: (e) => {
            var _a, _b, _c, _d;
            if (e.key === 'Enter') {
                (_b = (_a = cell === null || cell === void 0 ? void 0 : cell.column.columnDef.meta) === null || _a === void 0 ? void 0 : _a.isInlineEditCell) === null || _b === void 0 ? void 0 : _b.onEditChange(inputValue, cell);
                (_c = e.currentTarget.parentElement) === null || _c === void 0 ? void 0 : _c.focus();
                return;
            }
            else if (e.key === 'Escape') {
                (_d = e.currentTarget.parentElement) === null || _d === void 0 ? void 0 : _d.focus();
                return;
            }
            else if (e.key === 'ArrowDown' ||
                e.key === 'ArrowUp' ||
                e.key === 'ArrowLeft' ||
                e.key === 'ArrowRight') {
                e.stopPropagation();
            }
        }, onBlur: () => {
            setInputValue(value);
        } }));
};
const DropDownCell = ({ cell, options, portalTarget = document.body, }) => {
    var _a;
    if (!((_a = cell.column.columnDef.meta) === null || _a === void 0 ? void 0 : _a.isDropDownCell)) {
        return null;
    }
    const defaultOption = options.find((option) => { var _a; return option.value === ((_a = cell.getValue()) === null || _a === void 0 ? void 0 : _a.toString()); });
    return (_jsx(Dropdown, { fluid: true, hasBorder: false, style: { width: '100%' }, type: "select", size: "medium", selectProps: {
            defaultValue: defaultOption,
            options: options,
            menuPortalTarget: portalTarget,
            menuPosition: 'fixed',
            styles: {
                container: () => ({ width: '100%' }),
                control: () => ({
                    backgroundColor: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                }),
            },
            onChange: (e) => {
                var _a, _b;
                const value = e === null || e === void 0 ? void 0 : e.value;
                (_b = (_a = cell === null || cell === void 0 ? void 0 : cell.column.columnDef.meta) === null || _a === void 0 ? void 0 : _a.isDropDownCell) === null || _b === void 0 ? void 0 : _b.onChange(value === null || value === void 0 ? void 0 : value.toString(), cell);
            },
        } }));
};
export { ResizingBar, Header, Body, BodyCell, BodyRow, InlineEditCell, DropDownCell, RowActionCell, HeaderCell, HeaderTitle, Navigation, TableResults, RowsPerPage, Pagination, PaginationNumericButtons, PaginationArrowButton, PaginationNumericButton, NoDataPlaceholder, LoadingPlaceholder, Scrollable, };
//# sourceMappingURL=Components.js.map